import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Brain, Target, Users, Heart, Sparkles, MessageSquare } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const therapeuticServices = [
  {
    icon: Sparkles,
    title: "Tailored Psychotherapy",
    subtitle: "Adults & Teens",
    description: "CBT, Schema Therapy, and Mindfulness-based protocols for adolescents and adults requiring psychological support and emotional regulation."
  },
  {
    icon: Brain,
    title: "Neuro-Affirming Behavior Therapy",
    subtitle: "In-Clinic",
    description: "Evidence-based behavioral interventions including ABA, PBS, and sensory modulation protocols mapped to neurodevelopmental profiles."
  },
  {
    icon: MessageSquare,
    title: "Parenting Strategy Design",
    subtitle: "1-on-1 Coaching",
    description: "Personalized parenting strategies derived from your child's biopsychosocial profile for data-driven family intervention."
  },
  {
    icon: Users,
    title: "Family System Reconstruction",
    subtitle: "Clinical Mediation",
    description: "Neutral, clinical guidance to break negative cycles, improve communication, and rebuild peaceful family architecture."
  },
];

export const ServicesPage = () => {
  return (
    <div data-testid={SERVICES.section}>
      <Helmet>
        <title>Therapeutic Services | Modern Minds - Behavioural Therapy & Parenting Consultancy</title>
        <meta name="description" content="Expert therapeutic services including CBT, ABA, neuro-affirming behavior therapy, parenting coaching, and family system reconstruction at Modern Minds, Lucknow." />
        <link rel="canonical" href="https://www.modernminds.co.in/services" />
      </Helmet>

      {/* Hero Section with Glass Panel */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="glass-card p-8 sm:p-12 lg:p-16 text-center"
          >
            <motion.div variants={fadeInUp} className="gradient-divider mx-auto mb-6" />
            <motion.span
              variants={fadeInUp}
              className="inline-block bg-[#7C9885]/20 text-[#94A89D] text-xs font-body font-semibold px-3 py-1 rounded mb-4"
            >
              THERAPEUTIC SERVICES
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6"
            >
              Bridging Global Brain Science with Human Connection.
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-base sm:text-lg text-[#E2E8F0] max-w-2xl mx-auto"
            >
              Data-driven intervention protocols mapped to your child&apos;s unique cognitive and sensory profile.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Therapeutic Services Grid */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-4">
            {therapeuticServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={SERVICES.childTherapyCard}
                className="glass-card-teal p-5"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#3D9A8B]/20 rounded flex items-center justify-center flex-shrink-0">
                    <service.icon size={20} className="text-[#3D9A8B]" />
                  </div>
                  <div className="flex-1">
                    <span className="font-body text-[10px] text-[#778DA9] uppercase tracking-wider">
                      {service.subtitle}
                    </span>
                    <h3 className="font-heading text-lg font-bold text-white mb-2">
                      {service.title}
                    </h3>
                    <p className="font-body text-sm text-[#E0E1DD] leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Link to Assessments */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass-card-gold p-6"
          >
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <span className="inline-block bg-[#C9A962]/20 text-[#C9A962] text-xs font-body font-semibold px-3 py-1 rounded mb-3">
                  DIAGNOSTIC ASSESSMENTS
                </span>
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">
                  Need Diagnostic Clarity First?
                </h2>
                <p className="font-body text-sm text-[#E0E1DD] leading-relaxed mb-4">
                  Before therapeutic intervention, establish your child&apos;s biopsychosocial baseline with our gold-standard diagnostic mapping. ADOS-2, WISC-IV/V, and comprehensive psychometric profiling.
                </p>
                <Link
                  to="/assessments"
                  className="inline-flex items-center gap-2 btn-gold rounded px-5 py-2.5 font-body font-semibold text-sm"
                >
                  View Assessment Battery
                  <ArrowRight size={14} />
                </Link>
              </div>
              <div className="hidden md:flex justify-center">
                <div className="w-24 h-24 rounded bg-[#C9A962]/20 flex items-center justify-center">
                  <Target size={40} className="text-[#C9A962]" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 text-center"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
              Begin Therapeutic Journey
            </h2>
            <p className="font-body text-sm text-[#E0E1DD] mb-6 max-w-xl mx-auto">
              Schedule a clinical intake to determine the optimal therapeutic pathway for your child and family.
            </p>
            <a
              href="https://consult.modernminds.co.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 btn-teal rounded px-8 py-3 font-body font-semibold"
            >
              Schedule Consultation
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
